import React, { Fragment } from 'react';
import { withSnackbar, ProviderContext } from 'notistack';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CloudUploadOutlined } from '@material-ui/icons';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import FileUploadRow from '../../_shared/FileUploadRow';
import { ScaleLoader } from 'react-spinners';
import MaterialTable from 'material-table';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { ICaseService } from '../../../services/Interfaces/ICaseService';
import { ImportTemplateCodeEnum } from '../../../helpers/Constants';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ITariffService } from '../../../services/Interfaces/ITariffService';
import { getLocalization, MaterialTableIcons } from '../../_shared/TableSettings';
import {
  ImportWorkflowTariffsAttachment,
  ImportWorkflowTariffsRequestDto,
  WorkflowTariffImportItemDto
} from '../../../interfaces/Tariff';

interface IWorkflowTariffsImportOwnProps {
  workflowId: number;
  onClose?: () => void;
}

interface IWorkflowTariffsImportState {
  isImportDialogOpen: boolean;
  isLoadingImportData: boolean;
  isDownloading: boolean;
  attachments: ImportWorkflowTariffsAttachment[];
  importedItems: WorkflowTariffImportItemDto[];
}

type IWorkflowTariffsImportProps = ProviderContext & IWorkflowTariffsImportOwnProps;

class WorkflowTariffsImport extends React.PureComponent<
  IWorkflowTariffsImportProps,
  IWorkflowTariffsImportState
> {
  private translatorService!: ITranslatorService;
  private tariffService!: ITariffService;
  private caseService!: ICaseService;

  static contextType = ApplicationContext;

  state = {
    isImportDialogOpen: false,
    isLoadingImportData: false,
    isDownloading: false,
    attachments: [] as ImportWorkflowTariffsAttachment[],
    importedItems: [] as WorkflowTariffImportItemDto[]
  } as IWorkflowTariffsImportState;

  openImportDialog = () => {
    this.setState({
      isImportDialogOpen: true,
      isLoadingImportData: false,
      attachments: [],
      importedItems: []
    });
  };

  closeImportDialog = () => {
    this.setState({
      isImportDialogOpen: false,
      attachments: [],
      importedItems: []
    });

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleDownloadTemplate = async () => {
    if (this.state.isDownloading) return;
    this.setState({ isDownloading: true });
    try {
      const blob = await this.caseService.GetImportModel(
        ImportTemplateCodeEnum.WORKFLOW_TARIFF_IMPORT_TEMPLATE
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Import workflow tariffs.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('ERROR_MSG', 'Eroare'),
        { variant: 'error' }
      );
    } finally {
      this.setState({ isDownloading: false });
    }
  };

  handleUploadFileSelected = (file: File) => {
    const attachments = [...this.state.attachments];
    attachments.push({
      id: 0,
      file,
      fileName: file.name,
      uploadDate: new Date(),
      originalFileName: file.name
    });
    this.setState({ attachments });
  };

  handleUploadFileRemoved = () => {
    this.setState({ attachments: [] });
  };

  handleImport = async () => {
    if (this.state.attachments.length === 0) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate(
          'ERROR_MSG_FILE_NOT_SELECTED',
          'Va rugam sa selectati un fisier!'
        ),
        { variant: 'error' }
      );
      return;
    }
    this.setState({ isLoadingImportData: true });

    const request: ImportWorkflowTariffsRequestDto = {
      workflowId: this.props.workflowId,
      attachments: this.state.attachments
    };

    try {
      const result = await this.tariffService.ImportWorkflowTariffs(request);
      this.props.enqueueSnackbar(
        <div>
          <p>
            {this.translatorService.Tranlate(
              'ORGANIZATIONS_IMPORT_DATA_PROCESSED',
              'Date tratate: '
            )}{' '}
            {result.totalNumber}
          </p>
          <p>
            {this.translatorService.Tranlate(
              'ORGANIZATIONS_IMPORT_TREATED_SUCCESSFULLY',
              'Tratate cu succes: '
            )}{' '}
            {result.importedNumber}
          </p>
        </div>,
        { variant: 'info', autoHideDuration: 6000 }
      );
      this.setState({
        importedItems: result.items,
        isLoadingImportData: false
      });
    } catch {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('ERROR_MSG', 'Eroare'),
        { variant: 'error', autoHideDuration: 6000 }
      );
      this.setState({ isLoadingImportData: false });
    }
  };

  renderInfoRow = (
    iconNode: React.ReactNode,
    iconBgColor: string,
    title: string,
    description: React.ReactNode,
    actionNode: React.ReactNode
  ) => (
    <Paper
      variant="outlined"
      className="p-3 mb-3 d-flex flex-row align-items-center"
      style={{ borderRadius: 8 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{
          width: 44,
          height: 44,
          borderRadius: 6,
          backgroundColor: iconBgColor,
          marginRight: 16
        }}
      >
        {iconNode}
      </Box>
      <Box flexGrow={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </Box>
      {actionNode}
    </Paper>
  );

  renderAttachmentBox = () => {
    const attachment = this.state.attachments[0];

    return (
      <Box className="mt-2 mb-4">
        {this.renderInfoRow(
          <DescriptionOutlinedIcon style={{ color: '#FF7345' }} />,
          '#FFF5F2',
          this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_ALLOWED_FORMAT',
            'Format fisier acceptat: .XLSX'
          ),
          this.translatorService.Tranlate(
            'WORKFLOW_TARIFFS_IMPORT_ALLOWED_FORMAT_DESC',
            'Descarca modelul si pastreaza structura asteptata inainte de a importa tarifele.'
          ),
          <Button
            variant="contained"
            onClick={this.handleDownloadTemplate}
            disabled={this.state.isDownloading}
            color="secondary"
            startIcon={<GetAppOutlinedIcon />}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {this.translatorService.Tranlate(
              'ORGANIZATIONS_IMPORT_DOWNLOAD_TEMPLATE',
              'Descarca model'
            )}
          </Button>
        )}

        <FileUploadRow
          inputId="workflow-tariffs-import-file"
          title={this.translatorService.Tranlate(
            'WORKFLOW_TARIFFS_IMPORT_ADD_FILE',
            'Adauga fisier tarife'
          )}
          emptyDescription={this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_ADD_FILE_DESC',
            'Selecteaza un fisier .xlsx'
          )}
          uploadButtonLabel={this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_UPLOAD_FILE',
            'Incarca fisier'
          )}
          accept=".xls,.xlsx"
          fileName={attachment?.originalFileName ?? null}
          onFileSelected={this.handleUploadFileSelected}
          onFileRemoved={this.handleUploadFileRemoved}
        />
      </Box>
    );
  };

  getImportedColumns = () => [
    {
      field: 'partnerId',
      title: this.translatorService.Tranlate('WORKFLOW_TARIFFS_IMPORT_PARTNER_ID', 'Id Organizatie')
    },
    {
      field: 'partnerName',
      title: this.translatorService.Tranlate('WORKFLOW_TARIFFS_IMPORT_PARTNER_NAME', 'Nume organizatie')
    },
    {
      field: 'date',
      title: this.translatorService.Tranlate('TARIFF_EDIT_LABOR_TAB_DATE_LABEL', 'De la')
    },
    {
      field: 'tariffTypeGroup',
      title: this.translatorService.Tranlate('WORKFLOW_TARIFFS_IMPORT_TYPE', 'Tip')
    },
    {
      field: 'tariffType',
      title: this.translatorService.Tranlate('WORKFLOW_TARIFFS_IMPORT_CATEGORY', 'Categoria')
    },
    {
      field: 'vehicleType',
      title: this.translatorService.Tranlate('TARIFF_EDIT_LABOR_TAB_VEHICLE_TYPE_LABEL', 'Tip vehicul')
    },
    {
      field: 'jobType',
      title: this.translatorService.Tranlate('TARIFF_EDIT_LABOR_TAB_JOB_TYPE_LABEL', 'Tip Lucrare')
    },
    {
      field: 'insurer',
      title: this.translatorService.Tranlate('EDIT_TARIFF_INSURER_LABEL', 'Asigurator')
    },
    {
      field: 'brand',
      title: this.translatorService.Tranlate('TARIFF_EDIT_LABOR_TAB_BRAND_LABEL', 'Marca')
    },
    {
      field: 'value',
      title: this.translatorService.Tranlate('WORKFLOW_TARIFFS_IMPORT_VALUE', 'Valoare')
    },
    {
      field: 'marginValue',
      title: this.translatorService.Tranlate('TARIFF_EDIT_LABOR_TAB_MARGIN_VALUE_LABEL', 'Valoare Deviz')
    },
    {
      field: 'offerValue',
      title: this.translatorService.Tranlate('TARIFF_EDIT_LABOR_TAB_PROCENT_VALUE_LABEL', 'Procent')
    },
    {
      field: 'importMessage',
      title: this.translatorService.Tranlate(
        'MAVO_VEHICLES_VEHICLE_IMPORT_MESSAGE',
        'Rezultat import'
      )
    }
  ];

  renderImportedResultBox = () => {
    return this.state.importedItems.length > 0 ? (
      <MaterialTable
        columns={this.getImportedColumns()}
        data={this.state.importedItems}
        title=""
        icons={MaterialTableIcons}
        localization={getLocalization(this.translatorService)}
        options={{
          actionsColumnIndex: -1,
          addRowPosition: 'first',
          maxBodyHeight: 400,
          paging: false,
          sorting: false,
          search: false
        }}
        components={{
          Container: (props) => <Paper {...props} elevation={0} />
        }}
      />
    ) : (
      ''
    );
  };

  renderImportContent = () => {
    return (
      <ValidatorForm onSubmit={this.handleImport} instantValidate={true}>
        {this.renderAttachmentBox()}
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoadingImportData} />
        </div>
        {this.renderImportedResultBox()}
        <div className="text-right">
          <Button
            className="m-2"
            variant="outlined"
            color="default"
            onClick={this.closeImportDialog}
          >
            {this.translatorService.Tranlate('MAVO_VEHICLE_IMPORT_CLOSE_BTN', 'Renunta')}
          </Button>
          <Button
            className="m-2"
            variant="contained"
            color="secondary"
            type="submit"
            disabled={this.state.isLoadingImportData}
          >
            {this.translatorService.Tranlate('MAVO_VEHICLE_IMPORT_BTN', 'Importa')}
          </Button>
        </div>
      </ValidatorForm>
    );
  };

  render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.tariffService = (this.context as AppContext).tariffService;
    this.caseService = (this.context as AppContext).caseService;

    return (
      <Fragment>
        <Tooltip
          title={this.translatorService.Tranlate('Import', 'Importa')}
        >
          <IconButton
            aria-label="import"
            color="primary"
            className="m-2"
            onClick={this.openImportDialog}
          >
            <CloudUploadOutlined />
          </IconButton>
        </Tooltip>

        {this.state.isImportDialogOpen ? (
          <Dialog
            onClose={this.closeImportDialog}
            aria-labelledby="customized-dialog-title"
            open={this.state.isImportDialogOpen}
            fullScreen
            fullWidth
          >
            <DialogTitle
              id="customized-dialog-title"
              style={{
                color: '#ffffff',
                backgroundColor: 'var(--primary)',
                textAlign: 'center'
              }}
            >
              <span style={{ fontSize: '20px', lineHeight: '24px' }}>
                {this.translatorService.Tranlate(
                  'WORKFLOW_TARIFFS_IMPORT_MODAL_TITLE',
                  'Importa tarife'
                )}
              </span>
              <IconButton
                aria-label="close"
                className="position-absolute"
                style={{ right: 12, top: 12, color: '#ffffff' }}
                onClick={this.closeImportDialog}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box m={1} p={1}>
                {this.renderImportContent()}
              </Box>
            </DialogContent>
          </Dialog>
        ) : (
          ''
        )}
      </Fragment>
    );
  }
}

export default withSnackbar(WorkflowTariffsImport);
